import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDownTrayIcon, ArrowPathIcon, ClockIcon, DocumentTextIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { downloadExamDocument, getExamDocuments } from "../../../services/Exams/ExamsService";
import { getApiErrorMessage } from "../../../utils/apiError";
import { formatDateTime } from "../../../utils/formatDate";
import { Spinner, focusRing, quietButtonClass } from "../TestBuilder/ui";
import { keyRefusalText, languageName, sizeText, variantWord } from "./wordText";

/**
 * The exam's documents into entries, in the server's order (newest first): the two files of one export in the BTS
 * template (the key and the student copy: the same `job`, each the other's `pair`) are one entry {key, student};
 * a document of the old export (template "classic", or a server without the template) is one of its own. A key the
 * user may not have is left out of the list: its student copy (its `pair` naming a row that is not there) stands
 * alone, an entry {student}.
 */
export const documentEntries = (rows) => {
  const entries = [];
  const byExport = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row?.id) return;
    if (row.template !== "bts") {
      entries.push({ id: row.id, classic: row });
      return;
    }
    const exportKey = row.job || (row.pair ? [row.id, row.pair].sort().join(":") : row.id);
    let entry = byExport.get(exportKey);
    if (!entry) {
      entry = { id: exportKey, first: row };
      byExport.set(exportKey, entry);
      entries.push(entry);
    }
    entry[row.kind === "student" ? "student" : "key"] = row;
  });
  return entries;
};

// "Word · БТС · қазақша · НҰСҚА 3": what the export printed (its language, and the code its footer carries).
const entryTitle = (t, row) =>
  ["Word", t("exam_bank.documents.bts"), languageName(t, row.language, { inline: true }), row.code ? `${variantWord(row.language)} ${row.code}` : ""]
    .filter(Boolean)
    .join(" · ");

// A quiet Download: the spinner in place of its icon while its file comes, its clicks ignored, its focus kept.
function DownloadButton({ busy, label, ariaLabel, title, onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={() => !busy && onClick()}
      aria-disabled={busy || undefined}
      aria-busy={busy || undefined}
      aria-label={ariaLabel}
      title={title}
      className={`${quietButtonClass} aria-disabled:cursor-wait aria-disabled:opacity-60 ${className}`}
    >
      {busy ? <Spinner /> : <ArrowDownTrayIcon className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />}
      {label}
    </button>
  );
}

/**
 * The exam's Word documents under its tests, read again when `refreshKey` changes (an export ended, a file was
 * downloaded: the old rows count downloads). Nothing is shown while there are none. A file that is gone from the
 * storage (404 document_missing) says so on its entry. Keys the user may not have are left out by the server, which
 * says so (answers_hidden): those with questions whose answers are not theirs, and, for all but admins, every key made
 * before keys recorded their questions (every classic Word among them). One quiet line under the title names both and
 * the way to a key (the Word made again); a key refused at its download (403 answers_hidden) says why on its entry.
 */
export default function ExamDocuments({ examUuid, refreshKey = 0 }) {
  const [t, i18nInstance] = useTranslation();
  const language = i18nInstance.resolvedLanguage || i18nInstance.language;
  // null until the first read answers.
  const [rows, setRows] = useState(null);
  // Keys left out of the list (answers_hidden): {hidden, legacy} as the server counts them (keys_hidden), or null
  // counts from a server that only says some are; null when none are.
  const [hiddenKeys, setHiddenKeys] = useState(null);
  const keysHidden = !!hiddenKeys;
  const [loadFailed, setLoadFailed] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // Entry id -> the refusal of its last download: {text, held} (held: a key not given, said calmly).
  const [errors, setErrors] = useState({});
  // A read overtaken by a newer one is dropped.
  const readRef = useRef(0);

  const read = useCallback(
    async (signal) => {
      const gen = ++readRef.current;
      try {
        const data = await getExamDocuments({ params: { uuid: examUuid }, signal });
        if (gen !== readRef.current) return;
        setRows(Array.isArray(data?.documents) ? data.documents : []);
        const counts = data?.keys_hidden;
        setHiddenKeys(
          data?.answers_hidden === true
            ? { hidden: counts ? Number(counts.hidden) || 0 : null, legacy: counts ? Number(counts.legacy) || 0 : null }
            : null
        );
        setLoadFailed(false);
      } catch (err) {
        if (signal?.aborted || gen !== readRef.current) return;
        // The list stays as it was; a first read that failed says so (with a way to try again).
        setLoadFailed(true);
      }
    },
    [examUuid]
  );

  useEffect(() => {
    if (!examUuid) return undefined;
    const controller = new AbortController();
    read(controller.signal);
    return () => controller.abort();
  }, [examUuid, read, refreshKey]);

  const download = async (entryId, doc) => {
    setBusyId(doc.id);
    setErrors((prev) => ({ ...prev, [entryId]: null }));
    try {
      await downloadExamDocument(doc);
      read();
    } catch (err) {
      // A key refused as not the user's (403 answers_hidden) says why. The list is not read again: an old key's entry
      // would go from it, and this line with it.
      const held = err?.response ? keyRefusalText(t, err) : null;
      const text = !err?.response
        ? t("exam_bank.word.network")
        : held || getApiErrorMessage(t, err, "exam_bank.documents.download_error");
      setErrors((prev) => ({ ...prev, [entryId]: { text, held: !!held } }));
    } finally {
      setBusyId(null);
    }
  };

  if (rows === null) {
    if (loadFailed) {
      return (
        <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-200 pt-6 text-sm text-slate-600">
          <span>{t("exam_bank.documents.load_error")}</span>
          <button
            type="button"
            onClick={() => read()}
            className={`inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1 font-medium text-blue-700 hover:bg-blue-50 max-sm:min-h-[2.75rem] ${focusRing}`}
          >
            <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
            {t("exam_bank.word.retry")}
          </button>
        </div>
      );
    }
    return (
      <div className="mt-6 flex items-center justify-center gap-3 border-t border-slate-200 py-8 text-sm text-slate-500">
        <Spinner className="h-5 w-5" />
        <span>{t("exam_bank.documents.loading")}</span>
      </div>
    );
  }

  const entries = documentEntries(rows);
  // Only keys left out: the section says so all the same (its title then without a count of none).
  if (!entries.length && !keysHidden) return null;

  return (
    <section className="mt-6 border-t border-slate-200 pt-6" aria-labelledby="exam-documents-title" data-testid="exam-documents">
      <h3 id="exam-documents-title" className={`text-base font-semibold text-slate-900 ${keysHidden ? "mb-1" : "mb-4"}`}>
        {entries.length ? t("exam_bank.documents.title", { count: entries.length }) : t("exam_bank.documents.title_plain")}
      </h3>
      {/* Why keys are left out, by the server's counts (the way to a key with the old ones); a server without the
          counts gets the line that names both causes. */}
      {keysHidden && (
        <p className={`flex items-start gap-1.5 text-sm text-slate-500 ${entries.length ? "mb-4" : ""}`} data-testid="exam-documents-keys-hidden">
          <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
          <span className="min-w-0">
            {hiddenKeys.hidden === null || (!hiddenKeys.hidden && !hiddenKeys.legacy)
              ? t("exam_bank.documents.keys_hidden")
              : [
                  hiddenKeys.hidden ? t("exam_bank.documents.keys_hidden_closed", { count: hiddenKeys.hidden }) : null,
                  hiddenKeys.legacy ? t("exam_bank.documents.keys_hidden_legacy", { count: hiddenKeys.legacy }) : null,
                ]
                  .filter(Boolean)
                  .join(" ")}
          </span>
        </p>
      )}

      {entries.length > 0 && (
        <ul className="space-y-2">
          {entries.map((entry) => {
            const row = entry.classic || entry.first;
            const error = errors[entry.id];
            const meta = (
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                {entry.classic && <span>{t("exam_bank.documents.size", { size: sizeText(row.file_size, language) })}</span>}
                {entry.classic && <span>{t("exam_bank.documents.downloads", { count: row.download_count || 0 })}</span>}
                <span title={row.created_at} className="inline-flex items-center tabular-nums">
                  <ClockIcon className="mr-1 inline h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
                  {formatDateTime(row.created_at, language)}
                </span>
                {row.created_by && <span>{t("exam_bank.documents.created_by", { name: row.created_by })}</span>}
              </div>
            );
            const files = entry.classic
              ? [{ doc: entry.classic, label: t("buttons.download"), kind: "" }]
              : [
                  entry.key && { doc: entry.key, label: t("exam_bank.documents.key"), kind: t("exam_bank.word.download_key") },
                  entry.student && { doc: entry.student, label: t("exam_bank.documents.student"), kind: t("exam_bank.word.download_student") },
                ].filter(Boolean);
            return (
              <li
                key={entry.id}
                data-testid={entry.classic ? "exam-document-classic" : "exam-document-bts"}
                className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-xl border border-slate-200 bg-white p-3 sm:flex-nowrap sm:px-4"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500" aria-hidden="true">
                    <DocumentTextIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className={`text-sm font-medium text-slate-900 ${entry.classic ? "truncate" : "break-words"}`}>
                      {entry.classic ? row.filename : entryTitle(t, row)}
                    </h4>
                    {meta}
                    {error && (
                      <p role="alert" className={`mt-1 text-sm ${error.held ? "text-amber-800" : "text-red-700"}`} data-testid="exam-document-error">
                        {error.text}
                      </p>
                    )}
                  </div>
                </div>

                <div className="ml-auto flex shrink-0 items-center gap-1 max-sm:w-full max-sm:justify-end">
                  {files.map(({ doc, label, kind }) => (
                    <DownloadButton
                      key={doc.id}
                      busy={busyId === doc.id}
                      label={label}
                      title={[doc.filename, sizeText(doc.file_size, language)].filter(Boolean).join(" · ")}
                      ariaLabel={
                        kind
                          ? t("exam_bank.documents.download_kind_aria", { kind, name: doc.filename })
                          : `${t("buttons.download")}: ${doc.filename}`
                      }
                      onClick={() => download(entry.id, doc)}
                      className={entry.classic ? "" : "max-sm:flex-1"}
                    />
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
